import React, { useState, useRef, useEffect } from "react";
import {
  Play,
  SkipBack,
  SkipForward,
  Square,
  Volume2,
  VolumeX,
  AlertCircle,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface AudioMessageProps {
  url: string;
  prompt?: string;
}

interface EQSettings {
  low: number;
  mid: number;
  high: number;
}

export const AudioMessage: React.FC<AudioMessageProps> = ({ url, prompt }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [equalizerLevels, setEqualizerLevels] = useState([0.2, 0.5, 0.8]); // Animasyon için
  const [eqSettings, setEqSettings] = useState<EQSettings>({
    low: 0.5,
    mid: 0.5,
    high: 0.5,
  }); // Gerçek ayarlar
  const [isEqCustomized, setIsEqCustomized] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [showEqualizer, setShowEqualizer] = useState(true);

  const audioRef = useRef<HTMLAudioElement>(null);
  const dialRef = useRef<HTMLDivElement>(null);
  const rotationRef = useRef<number>(0);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const rotationTimerRef = useRef<number | null>(null);
  const eqAnimationRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Audio element için filtreleri oluştur
  const lowpassFilterRef = useRef<BiquadFilterNode | null>(null);
  const bandpassFilterRef = useRef<BiquadFilterNode | null>(null);
  const highpassFilterRef = useRef<BiquadFilterNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  // Ekran genişliğini kontrol et - 768px altı mobil olarak kabul edilsin
  const [isMobile, setIsMobile] = useState(false);

  // Ekran boyutunu izle
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      // Mobil görünümde başlangıçta ekolayzeri gizle
      if (window.innerWidth < 768) {
        setShowEqualizer(false);
      } else {
        setShowEqualizer(true);
      }
    };

    // İlk yükleme kontrolü
    checkScreenSize();

    // Pencere boyutu değiştiğinde kontrol et
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // AudioContext kurulumu
  const setupAudioFilters = () => {
    // Önceki bağlantıları temizle
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
      } catch (e) {}
    }

    try {
      const audio = audioRef.current;
      if (!audio) return false;

      // AudioContext oluştur
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const ctx = audioContextRef.current;

      // Ses kaynağı oluştur
      sourceNodeRef.current = ctx.createMediaElementSource(audio);

      // Filtreler oluştur
      lowpassFilterRef.current = ctx.createBiquadFilter();
      lowpassFilterRef.current.type = "lowshelf";
      lowpassFilterRef.current.frequency.value = 320;
      lowpassFilterRef.current.gain.value = 0; // Başlangıçta nötr

      bandpassFilterRef.current = ctx.createBiquadFilter();
      bandpassFilterRef.current.type = "peaking";
      bandpassFilterRef.current.frequency.value = 1000;
      bandpassFilterRef.current.Q.value = 1;
      bandpassFilterRef.current.gain.value = 0; // Başlangıçta nötr

      highpassFilterRef.current = ctx.createBiquadFilter();
      highpassFilterRef.current.type = "highshelf";
      highpassFilterRef.current.frequency.value = 3200;
      highpassFilterRef.current.gain.value = 0; // Başlangıçta nötr

      // Ana gain node
      gainNodeRef.current = ctx.createGain();
      gainNodeRef.current.gain.value = 1;

      // Bağlantıları yap
      sourceNodeRef.current
        .connect(lowpassFilterRef.current)
        .connect(bandpassFilterRef.current)
        .connect(highpassFilterRef.current)
        .connect(gainNodeRef.current)
        .connect(ctx.destination);

      return true;
    } catch (error) {
      console.error("AudioContext kurulumu hatası:", error);
      return false;
    }
  };

  // EQ ayarlarını filtreler üzerine uygula
  const applyEQSettings = () => {
    if (
      !lowpassFilterRef.current ||
      !bandpassFilterRef.current ||
      !highpassFilterRef.current
    )
      return;

    // -10 ile +10 arasında değerler (0.5 orta nokta olarak)
    lowpassFilterRef.current.gain.value = (eqSettings.low - 0.5) * 20;
    bandpassFilterRef.current.gain.value = (eqSettings.mid - 0.5) * 20;
    highpassFilterRef.current.gain.value = (eqSettings.high - 0.5) * 20;
  };

  // Ses elementi olay dinleyicileri
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
    };

    const setAudioTime = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlayEnd = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      stopRotation();
      stopEqAnimation();
    };

    const handleAudioError = (e: Event) => {
      console.error("Ses dosyası yüklenirken hata oluştu:", e);
      setAudioError(
        "Ses dosyası oynatılamadı. Lütfen internet bağlantınızı kontrol edin."
      );
      setIsPlaying(false);
    };

    const handlePlaying = () => {
      setIsPlaying(true);
      setAudioError(null);
      startEqAnimation();
    };

    const handlePause = () => {
      setIsPlaying(false);
      stopEqAnimation();
    };

    // Olay dinleyicileri ekle
    audio.addEventListener("loadeddata", setAudioData);
    audio.addEventListener("loadedmetadata", setAudioData);
    audio.addEventListener("timeupdate", setAudioTime);
    audio.addEventListener("ended", handlePlayEnd);
    audio.addEventListener("error", handleAudioError);
    audio.addEventListener("playing", handlePlaying);
    audio.addEventListener("pause", handlePause);

    // Başlangıç ses seviyesini ayarla
    audio.volume = volume;

    // Temizlik
    return () => {
      audio.removeEventListener("loadeddata", setAudioData);
      audio.removeEventListener("loadedmetadata", setAudioData);
      audio.removeEventListener("timeupdate", setAudioTime);
      audio.removeEventListener("ended", handlePlayEnd);
      audio.removeEventListener("error", handleAudioError);
      audio.removeEventListener("playing", handlePlaying);
      audio.removeEventListener("pause", handlePause);
      stopRotation();
      stopEqAnimation();

      // AudioContext temizleme
      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.disconnect();
        } catch (e) {}
      }

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        try {
          audioContextRef.current.close();
        } catch (e) {}
      }
    };
  }, []);

  // EQ ayarları değiştiğinde uygula
  useEffect(() => {
    if (isEqCustomized) {
      applyEQSettings();
    }
  }, [eqSettings, isEqCustomized]);

  // Dairesel döndürme animasyonu
  const startRotation = () => {
    if (rotationTimerRef.current !== null) return;

    const animate = () => {
      if (!isPlaying) return;
      rotationRef.current = (rotationRef.current + 1) % 360;
      if (dialRef.current) {
        dialRef.current.style.transform = `rotate(${rotationRef.current}deg)`;
      }
      rotationTimerRef.current = requestAnimationFrame(animate);
    };

    rotationTimerRef.current = requestAnimationFrame(animate);
  };

  const stopRotation = () => {
    if (rotationTimerRef.current !== null) {
      cancelAnimationFrame(rotationTimerRef.current);
      rotationTimerRef.current = null;
    }
  };

  // Ekolayzer animasyonu (gerçek veri olmadan)
  const startEqAnimation = () => {
    if (eqAnimationRef.current !== null) return;

    const animate = () => {
      if (!isPlaying) return;

      // Eğer kullanıcı EQ'yu özelleştirmişse, animasyonu EQ ayarlarına göre yap
      if (isEqCustomized) {
        setEqualizerLevels([
          Math.max(
            0.1,
            Math.min(1, eqSettings.low + (Math.random() * 0.2 - 0.1))
          ),
          Math.max(
            0.1,
            Math.min(1, eqSettings.mid + (Math.random() * 0.2 - 0.1))
          ),
          Math.max(
            0.1,
            Math.min(1, eqSettings.high + (Math.random() * 0.2 - 0.1))
          ),
        ]);
      } else {
        // Standart rastgele animasyon
        setEqualizerLevels([
          Math.max(0.1, Math.min(1, 0.2 + Math.random() * 0.5)),
          Math.max(0.1, Math.min(1, 0.5 + Math.random() * 0.4)),
          Math.max(0.1, Math.min(1, 0.3 + Math.random() * 0.6)),
        ]);
      }

      eqAnimationRef.current = setTimeout(animate, 150);
    };

    animate();
  };

  const stopEqAnimation = () => {
    if (eqAnimationRef.current !== null) {
      clearTimeout(eqAnimationRef.current);
      eqAnimationRef.current = null;
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      stopRotation();
      stopEqAnimation();
      setIsPlaying(false);
    } else {
      // İlk oynatma için AudioContext kurulumu
      if (!audioContextRef.current && !sourceNodeRef.current) {
        setupAudioFilters();
        // EQ ayarlarını uygula
        if (isEqCustomized) {
          applyEQSettings();
        }
      }

      // Ses seviyesini ayarla
      audio.volume = volume;
      audio.muted = isMuted;

      // Sesi oynat
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            startRotation();
            startEqAnimation();
            setAudioError(null);
          })
          .catch((error) => {
            console.error("Ses oynatılırken hata oluştu:", error);
            setAudioError(
              "Ses oynatılamadı. Tarayıcı otomatik oynatmaya izin vermiyor olabilir."
            );
            setIsPlaying(false);
          });
      } else {
        // Older browsers may not return a promise
        setIsPlaying(true);
        startRotation();
        startEqAnimation();
      }
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const newMuteState = !isMuted;
    audio.muted = newMuteState;
    setIsMuted(newMuteState);
  };

  const changeVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  // EQ ayarlarını değiştir
  const changeEQSetting = (band: keyof EQSettings, value: number) => {
    if (!isEqCustomized && audioRef.current?.paused === false) {
      // İlk kez EQ ayarlanıyorsa ve ses çalıyorsa AudioContext kur
      if (!audioContextRef.current) {
        setupAudioFilters();
      }
      setIsEqCustomized(true);
    }

    setEqSettings((prev) => ({ ...prev, [band]: value }));
    // Duruma göre animasyonları başlat
    if (isPlaying && eqAnimationRef.current === null) {
      startEqAnimation();
    }
  };

  const handleProgressChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const progressBar = progressBarRef.current;

    if (!audio || !progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const percent =
      Math.min(Math.max(0, e.clientX - rect.x), rect.width) / rect.width;
    audio.currentTime = percent * duration;
    setCurrentTime(percent * duration);
  };

  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, audio.currentTime - 10);
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.min(duration, audio.currentTime + 10);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";

    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Ses kıskacı için indeksi hesaplayalım
  const getMarkerPosition = (percentage: number) => {
    const angle = percentage * 270 - 135; // -135 ile 135 derece arası
    const radians = angle * (Math.PI / 180);
    const radius = 50 - 6; // Merkeze olan mesafe (50%) - marker boyutu/2

    const x = 50 + radius * Math.cos(radians);
    const y = 50 + radius * Math.sin(radians);

    return { x, y };
  };

  const markerPos = getMarkerPosition(currentTime / duration || 0);

  // Dairesel çizgiler için SVG yolları
  const generateArcPath = (percentage: number, radius: number) => {
    if (isNaN(percentage)) percentage = 0;
    percentage = Math.min(1, Math.max(0, percentage));

    const angleStart = -135; // Sol alt
    const angleSweep = percentage * 270; // 270 derece maksimum yay

    const startRad = (angleStart * Math.PI) / 180;
    const endRad = ((angleStart + angleSweep) * Math.PI) / 180;

    // SVG için başlangıç ve bitiş koordinatları
    const startX = 50 + radius * Math.cos(startRad);
    const startY = 50 + radius * Math.sin(startRad);
    const endX = 50 + radius * Math.cos(endRad);
    const endY = 50 + radius * Math.sin(endRad);

    // Büyük yay bayrağı (90 dereceden büyük yay için 1)
    const largeArcFlag = angleSweep <= 180 ? "0" : "1";

    return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
  };

  // Ticks (çentik) oluşturma
  const ticks = Array.from({ length: 50 }, (_, i) => {
    const angle = (i / 50) * 270 - 135; // -135 ile 135 derece arası
    const radians = angle * (Math.PI / 180);
    const outerRadius = 47;
    const innerRadius = 43;

    const x1 = 50 + outerRadius * Math.cos(radians);
    const y1 = 50 + outerRadius * Math.sin(radians);
    const x2 = 50 + innerRadius * Math.cos(radians);
    const y2 = 50 + innerRadius * Math.sin(radians);

    return { x1, y1, x2, y2, active: i / 50 <= (currentTime / duration || 0) };
  });

  // SVG'nin hemen üstüne, event handler fonksiyonları ve gerekli state'ler:
  const [isSeeking, setIsSeeking] = useState(false);

  // Dairesel seek fonksiyonu
  const handleCircleSeek = (
    e: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>
  ) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    // SVG 100x100, merkez 50,50
    const dx = x - 50;
    const dy = y - 50;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI); // -180 ile 180 arası
    // -135 ile 135 arası normalize et
    angle = angle < -135 ? 225 : angle;
    angle = Math.max(-135, Math.min(135, angle));
    const percent = (angle + 135) / 270;
    const newTime = percent * duration;
    // Debug logları

    if (!isNaN(newTime) && duration > 0) {
      setCurrentTime(newTime);
      if (audioRef.current) {
        audioRef.current.currentTime = newTime;
      }
    }
  };

  const handleCircleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    setIsSeeking(true);
    handleCircleSeek(e);
  };
  const handleCircleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isSeeking) handleCircleSeek(e);
  };
  const handleCircleMouseUp = () => {
    setIsSeeking(false);
  };
  // Touch eventler (mobilde kullanılmaz ama eklenebilir)
  const handleCircleTouchStart = (e: React.TouchEvent<SVGSVGElement>) => {
    setIsSeeking(true);
    handleCircleSeek(e);
  };
  const handleCircleTouchMove = (e: React.TouchEvent<SVGSVGElement>) => {
    if (isSeeking) handleCircleSeek(e);
  };
  const handleCircleTouchEnd = () => {
    setIsSeeking(false);
  };

  return (
    <div className="mt-2">
      {prompt && (
        <p className="text-xs text-muted-foreground mb-2 italic">"{prompt}"</p>
      )}

      <div className="rounded-xl overflow-hidden bg-card/30 border border-border p-4 shadow-md backdrop-blur-sm">
        {/* crossOrigin özelliği eklendi */}
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          crossOrigin="anonymous"
        />

        <div className="flex flex-col">
          {/* Hata mesajı */}
          {audioError && (
            <div className="mb-3 p-2 flex items-center gap-2 text-xs text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded-md">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{audioError}</p>
            </div>
          )}

          {/* Mobil görünüm her zaman gösteriliyor */}
          {isMobile && (
            <div className="flex flex-col space-y-2">
              {/* Mobil kontroller */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={skipBackward}
                    className="h-10 w-10 rounded-full bg-background/80 text-foreground flex items-center justify-center hover:bg-background transition-colors"
                  >
                    <SkipBack className="h-4 w-4" />
                  </button>

                  <button
                    onClick={togglePlay}
                    className="h-10 w-10 rounded-full bg-background/80 text-foreground flex items-center justify-center hover:bg-background transition-colors"
                  >
                    {isPlaying ? (
                      <Square className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4 ml-0.5" />
                    )}
                  </button>

                  <button
                    onClick={skipForward}
                    className="h-10 w-10 rounded-full bg-background/80 text-foreground flex items-center justify-center hover:bg-background transition-colors"
                  >
                    <SkipForward className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowEqualizer(!showEqualizer)}
                    className={cn(
                      "h-8 w-8 rounded-full text-foreground flex items-center justify-center transition-colors",
                      showEqualizer ? "bg-primary/20" : "bg-background/80"
                    )}
                  >
                    <Settings className="h-4 w-4" />
                  </button>

                  <button
                    onClick={toggleMute}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {isMuted ? (
                      <VolumeX className="h-4 w-4" />
                    ) : (
                      <Volume2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Mobil zaman ve ilerleme çubuğu */}
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {formatTime(currentTime)}
                </span>

                {/* Duration barı için underlay ve dolan kısmı */}
                <div className="relative flex-1 h-2 flex items-center">
                  {/* Underlay çizgi */}
                  <div className="absolute left-0 right-0 h-2 bg-gray-300 dark:bg-gray-600 rounded-full z-0" />
                  {/* Geçen süreyi gösteren koyu kısım */}
                  <div
                    className="absolute left-0 h-2 bg-primary rounded-full z-10 transition-all duration-100"
                    style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                  />
                  {/* Üstte tıklanabilir alan (progress bar) */}
                  <div
                    ref={progressBarRef}
                    onClick={handleProgressChange}
                    className="relative w-full h-2 cursor-pointer z-20"
                    style={{ background: "transparent" }}
                  />
                </div>

                <span className="text-xs font-medium text-muted-foreground">
                  {formatTime(duration)}
                </span>
              </div>

              {/* Mobil ekolayzer görüntüsü kapalıysa ses seviyesi kontrolü */}
              {!showEqualizer && (
                <div className="flex items-center justify-end gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">Ses</span>
                  <div className="relative w-12 h-4 flex items-center">
                    {/* Underlay çizgi */}
                    <div className="absolute left-0 right-0 h-1 bg-gray-300 dark:bg-gray-600 rounded-full z-0" />
                    {/* Slider */}
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={changeVolume}
                      className={cn(
                        "relative z-10 w-12 h-1.5 bg-transparent appearance-none",
                        "focus:outline-none [&::-webkit-slider-thumb]:appearance-none [&::-moz-range-thumb]:appearance-none",
                        "[&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary",
                        "[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary"
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Ekolayzer kısmı - Mobilde ayarlar açıkken veya masaüstünde gösteriliyor */}
          <AnimatePresence>
            {(!isMobile || showEqualizer) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                {/* Mobilde ayarlar açıkken sadece ekolayzer kontrolleri göster */}
                {isMobile ? (
                  <div className="mt-4 pb-1">
                    <div className="flex items-center justify-center mb-2">
                      <div className="h-px w-10 bg-muted-foreground/20"></div>
                    </div>

                    {/* Mobil ekolayzer kontrolleri - Daha küçük */}
                    <div className="flex items-center justify-center gap-8 h-20">
                      {["low", "mid", "high"].map((band, index) => (
                        <div
                          key={band}
                          className="flex flex-col items-center h-full"
                        >
                          {/* Ana kontrol alanı */}
                          <div className="relative h-14 w-6">
                            {/* Underlay çizgi (dikey) */}
                            <div className="absolute left-1/2 top-0 -translate-x-1/2 w-1 h-full bg-gray-300 dark:bg-gray-600 rounded-full z-0" />
                            {/* Arka plan */}
                            <div className="h-full w-full bg-muted/20 rounded-lg" />
                            {/* Orta dikey çizgi */}
                            <div className="absolute h-full w-0.5 bg-foreground/30 left-1/2 transform -translate-x-1/2" />
                            {/* Animasyon çubuğu */}
                            <div className="absolute bottom-0 left-0 right-0 rounded-b-lg overflow-hidden">
                              <motion.div
                                animate={{
                                  height: `${equalizerLevels[index] * 100}%`,
                                }}
                                transition={{ type: "spring", damping: 10 }}
                                className={cn(
                                  "w-full",
                                  isEqCustomized
                                    ? "bg-primary/50"
                                    : "bg-primary/60"
                                )}
                                style={{ minHeight: "4px" }}
                              />
                            </div>
                            {/* Kullanıcı kontrolü */}
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={eqSettings[band as keyof EQSettings]}
                              onChange={(e) =>
                                changeEQSetting(
                                  band as keyof EQSettings,
                                  parseFloat(e.target.value)
                                )
                              }
                              className="absolute inset-0 w-full h-full opacity-0 cursor-ns-resize z-10"
                              style={{
                                WebkitAppearance: "slider-vertical",
                              }}
                            />
                            {/* İşaretçi (daire olarak değiştirildi) */}
                            <div
                              className="absolute left-0 right-0 flex items-center justify-center transition-all duration-200 pointer-events-none z-20"
                              style={{
                                bottom: `calc(${
                                  eqSettings[band as keyof EQSettings] * 100
                                }%)`,
                                transform: "translateY(50%)",
                              }}
                            >
                              <div className="w-4 h-4 bg-primary rounded-full shadow-md flex items-center justify-center border border-primary/30">
                                <div className="w-1 h-1 bg-white rounded-full opacity-80"></div>
                              </div>
                            </div>
                          </div>
                          {/* Etiket */}
                          <span className="text-xs font-medium text-foreground mt-1">
                            {band === "low"
                              ? "Low"
                              : band === "mid"
                              ? "Mid"
                              : "High"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Masaüstü için tam kontroller
                  <>
                    {/* Dairesel kontroller ve ekolayzer */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={skipBackward}
                          className="h-12 w-12 rounded-full bg-background/80 text-foreground flex items-center justify-center hover:bg-background transition-colors"
                        >
                          <SkipBack className="h-5 w-5" />
                        </button>

                        <button
                          onClick={togglePlay}
                          className="h-12 w-12 rounded-full bg-background/80 text-foreground flex items-center justify-center hover:bg-background transition-colors"
                        >
                          {isPlaying ? (
                            <Square className="h-5 w-5" />
                          ) : (
                            <Play className="h-5 w-5 ml-0.5" />
                          )}
                        </button>

                        <button
                          onClick={skipForward}
                          className="h-12 w-12 rounded-full bg-background/80 text-foreground flex items-center justify-center hover:bg-background transition-colors"
                        >
                          <SkipForward className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="relative h-32 w-32">
                        {/* Dairesel ilerleme kontrolü */}
                        <svg
                          viewBox="0 0 100 100"
                          className="absolute inset-0 rotate-180 transform"
                          onMouseDown={handleCircleMouseDown}
                          onMouseMove={handleCircleMouseMove}
                          onMouseUp={handleCircleMouseUp}
                          onMouseLeave={handleCircleMouseUp}
                          onTouchStart={handleCircleTouchStart}
                          onTouchMove={handleCircleTouchMove}
                          onTouchEnd={handleCircleTouchEnd}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Gri arka plan çizgisi */}
                          <path
                            d={generateArcPath(1, 40)}
                            fill="none"
                            stroke="rgba(var(--muted)/0.3)"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />

                          {/* İlerleme çizgisi */}
                          <path
                            d={generateArcPath(currentTime / duration || 0, 40)}
                            fill="none"
                            stroke="var(--primary)"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                          />

                          {/* Çentikler */}
                          {ticks.map((tick, i) => (
                            <line
                              key={i}
                              x1={tick.x1}
                              y1={tick.y1}
                              x2={tick.x2}
                              y2={tick.y2}
                              stroke={
                                tick.active
                                  ? "var(--primary)"
                                  : "rgba(var(--muted)/0.3)"
                              }
                              strokeWidth={i % 5 === 0 ? "1" : "0.5"}
                            />
                          ))}

                          {/* Ses kıskacı */}
                          <circle
                            cx={markerPos.x}
                            cy={markerPos.y}
                            r="3.5"
                            fill="var(--primary)"
                          />
                        </svg>

                        {/* Orta kısımdaki dönen eleman */}
                        <div
                          ref={dialRef}
                          className="absolute inset-0 flex items-center justify-center"
                          style={{ pointerEvents: "none" }}
                        >
                          <div className="h-20 w-20 rounded-full flex items-center justify-center bg-background/90 border border-primary/10">
                            <AnimatePresence>
                              {isPlaying ? (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  exit={{ scale: 0 }}
                                  className="h-full w-full flex items-center justify-center"
                                >
                                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center group">
                                    <motion.div
                                      animate={{ scale: [0.9, 1.1, 0.9] }}
                                      transition={{
                                        repeat: Infinity,
                                        duration: 2,
                                      }}
                                      className="h-6 w-6 rounded-full bg-primary"
                                    />
                                  </div>
                                </motion.div>
                              ) : (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  exit={{ scale: 0 }}
                                  className="text-primary text-xs font-medium"
                                >
                                  {formatTime(currentTime)} /{" "}
                                  {formatTime(duration)}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>

                      {/* Tamamen yenilenmiş ekolayzer kontrolleri */}
                      <div className="flex items-center gap-6 h-24">
                        {["low", "mid", "high"].map((band, index) => (
                          <div
                            key={band}
                            className="flex flex-col items-center h-full"
                          >
                            {/* Yukarı ok */}
                            <div className="text-foreground mb-1 opacity-70">
                              <svg
                                width="14"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M7 0L14 8H0L7 0Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>

                            {/* Ana kontrol alanı */}
                            <div className="relative h-16 w-8">
                              {/* Underlay çizgi (dikey) */}
                              <div className="absolute left-1/2 top-0 -translate-x-1/2 w-1 h-full bg-gray-300 dark:bg-gray-600 rounded-full z-0" />
                              {/* Arka plan */}
                              <div className="h-full w-full bg-muted/20 rounded-lg" />
                              {/* Orta dikey çizgi */}
                              <div className="absolute h-full w-0.5 bg-foreground/30 left-1/2 transform -translate-x-1/2" />
                              {/* Animasyon çubuğu */}
                              <div className="absolute bottom-0 left-0 right-0 rounded-b-lg overflow-hidden">
                                <motion.div
                                  animate={{
                                    height: `${equalizerLevels[index] * 100}%`,
                                  }}
                                  transition={{ type: "spring", damping: 10 }}
                                  className={cn(
                                    "w-full",
                                    isEqCustomized
                                      ? "bg-primary/50"
                                      : "bg-primary/60"
                                  )}
                                  style={{ minHeight: "4px" }}
                                />
                              </div>
                              {/* Çizgiler */}
                              <div className="absolute inset-0 flex flex-col justify-between py-2 items-center pointer-events-none">
                                <div className="w-4 h-0.5 bg-foreground/30 rounded-full"></div>
                                <div className="w-6 h-0.5 bg-foreground/40 rounded-full"></div>
                                <div className="w-4 h-0.5 bg-foreground/30 rounded-full"></div>
                              </div>
                              {/* Kullanıcı kontrolü */}
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={eqSettings[band as keyof EQSettings]}
                                onChange={(e) =>
                                  changeEQSetting(
                                    band as keyof EQSettings,
                                    parseFloat(e.target.value)
                                  )
                                }
                                className="absolute inset-0 w-full h-full opacity-0 cursor-ns-resize z-10"
                                style={{
                                  WebkitAppearance: "slider-vertical",
                                }}
                              />
                              {/* İşaretçi (daire olarak değiştirildi) */}
                              <div
                                className="absolute left-0 right-0 flex items-center justify-center transition-all duration-200 pointer-events-none z-20"
                                style={{
                                  bottom: `calc(${
                                    eqSettings[band as keyof EQSettings] * 100
                                  }%)`,
                                  transform: "translateY(50%)",
                                }}
                              >
                                <div className="w-5 h-5 bg-primary rounded-full shadow-md flex items-center justify-center border border-primary/30">
                                  <div className="w-1.5 h-1.5 bg-white rounded-full opacity-80"></div>
                                </div>
                              </div>
                            </div>
                            {/* Aşağı ok */}
                            <div className="text-foreground mt-1 opacity-70">
                              <svg
                                width="14"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M7 8L0 0H14L7 8Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            {/* Etiket */}
                            <span className="text-xs font-medium text-foreground mt-1">
                              {band === "low"
                                ? "Low"
                                : band === "mid"
                                ? "Mid"
                                : "High"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Masaüstü için zaman çubuğu */}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        {formatTime(currentTime)}
                      </span>

                      {/* Duration barı için underlay ve dolan kısmı */}
                      <div className="relative flex-1 h-2 flex items-center">
                        {/* Underlay çizgi */}
                        <div className="absolute left-0 right-0 h-2 bg-gray-300 dark:bg-gray-600 rounded-full z-0" />
                        {/* Geçen süreyi gösteren koyu kısım */}
                        <div
                          className="absolute left-0 h-2 bg-primary rounded-full z-10 transition-all duration-100"
                          style={{
                            width: `${(currentTime / duration) * 100 || 0}%`,
                          }}
                        />
                        {/* Üstte tıklanabilir alan (progress bar) */}
                        <div
                          ref={progressBarRef}
                          onClick={handleProgressChange}
                          className="relative w-full h-2 cursor-pointer z-20"
                          style={{ background: "transparent" }}
                        />
                      </div>

                      <span className="text-xs font-medium text-muted-foreground">
                        {formatTime(duration)}
                      </span>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
